import type { IncomingMessage } from 'http';
import type { TLSSocket } from 'tls';
import { SDK_NAME, SDK_VERSION } from '../../constants';
import { truncateValue, truncationSize } from '../../utils';

class DataDomeModule {
    requestTimeMicros: number;
    name: string;
    version: string;
    constructor() {
        this.requestTimeMicros = Date.now() * 1000;
        this.name = SDK_NAME;
        this.version = SDK_VERSION;
    }
}

function tryDecode(input: string): string {
    try {
        return decodeURIComponent(input);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
        return input;
    }
}

function parseCookieString(input: string): Map<string, string> {
    const cookies = new Map();

    input.split(/; */).forEach((pair: string) => {
        let eqIndex = pair.indexOf('=');

        if (eqIndex > 0) {
            const key = pair.substring(0, eqIndex).trim();
            let value = pair.substring(++eqIndex, eqIndex + pair.length).trim();

            if (value[0] === '"') {
                value = value.slice(1, -1);
            }

            if (!cookies.has(key)) {
                cookies.set(key, tryDecode(value));
            }
        }
    });

    return cookies;
}

export interface RequestMetadata {
    addr?: string;
    accept?: string;
    acceptLanguage?: string;
    contentType?: string;
    method?: string;
    host?: string;
    port?: number;
    protocol?: string;
    userAgent?: string;
    referer?: string;
    xRealIp?: string;
    xForwardedForIp?: string;
    acceptEncoding?: string;
    serverHostname?: string;
    from?: string;
    request?: string;
    origin?: string;
    connection?: string;
    acceptCharset?: string;
    clientID?: string;
    secCHUA?: string;
    secCHUAMobile?: string;
    secCHUAPlatform?: string;
    secCHUAArch?: string;
    secCHUAFullVersionList?: string;
    secCHUAModel?: string;
    secCHDeviceMemory?: string;
}

class DataDomeHeaders {
    addr: string;
    accept: string;
    acceptLanguage: string;
    contentType: string;
    method: string;
    host: string;
    port: number;
    protocol: string;
    userAgent: string;
    referer: string;
    xRealIp: string;
    xForwardedForIp: string;
    acceptEncoding: string;
    serverHostname: string;
    from: string;
    request: string;
    origin: string;
    connection: string;
    acceptCharset: string;
    clientID: string;
    secCHUA?: string;
    secCHUAMobile?: string;
    secCHUAPlatform?: string;
    secCHUAArch?: string;
    secCHUAFullVersionList?: string;
    secCHUAModel?: string;
    secCHDeviceMemory?: string;

    constructor(request: IncomingMessage, requestMetadata?: RequestMetadata) {
        this.addr = requestMetadata?.addr ?? request.socket.remoteAddress ?? '127.0.0.1';
        this.method = requestMetadata?.method ?? request.method ?? '';
        this.port = requestMetadata?.port ?? this.getPort(request);
        this.protocol = requestMetadata?.protocol ?? this.getProtocol(request);
        this.contentType = truncateValue(
            requestMetadata?.contentType ?? request.headers['content-type'],
            truncationSize('ContentType')
        );
        this.accept = truncateValue(
            requestMetadata?.accept ?? request.headers['accept'],
            truncationSize('Accept')
        );
        this.acceptLanguage = truncateValue(
            requestMetadata?.acceptLanguage ?? request.headers['accept-language'],
            truncationSize('AcceptLanguage')
        );
        this.host = truncateValue(
            requestMetadata?.host ?? request.headers['host'],
            truncationSize('Host')
        );
        this.userAgent = truncateValue(
            requestMetadata?.userAgent ?? request.headers['user-agent'],
            truncationSize('UserAgent')
        );
        this.referer = truncateValue(
            requestMetadata?.referer ?? request.headers['referer'],
            truncationSize('Referer')
        );
        this.xRealIp = truncateValue(
            requestMetadata?.xRealIp ?? (request.headers['x-real-ip'] as string),
            truncationSize('XRealIp')
        );
        this.xForwardedForIp = truncateValue(
            requestMetadata?.xForwardedForIp ?? (request.headers['x-forwarded-for'] as string),
            truncationSize('XForwardedForIp')
        );
        this.acceptEncoding = truncateValue(
            requestMetadata?.acceptEncoding ?? (request.headers['accept-encoding'] as string),
            truncationSize('AcceptEncoding')
        );
        this.serverHostname = truncateValue(
            requestMetadata?.serverHostname ?? request.headers['host'],
            truncationSize('ServerHostname')
        );
        this.from = truncateValue(
            requestMetadata?.from ?? request.headers['from'],
            truncationSize('From')
        );
        this.origin = truncateValue(
            requestMetadata?.origin ?? request.headers['origin'],
            truncationSize('Origin')
        );
        this.connection = truncateValue(
            requestMetadata?.connection ?? request.headers['connection'],
            truncationSize('Connection')
        );
        this.acceptCharset = truncateValue(
            requestMetadata?.acceptCharset ?? (request.headers['accept-charset'] as string),
            truncationSize('AcceptCharset')
        );
        if (requestMetadata?.secCHUA) {
            this.secCHUA = truncateValue(requestMetadata?.secCHUA, truncationSize('SecCHUA'));
        } else if (request.headers['sec-ch-ua']) {
            this.secCHUA = truncateValue(
                request.headers['sec-ch-ua'] as string,
                truncationSize('SecCHUA')
            );
        }
        if (requestMetadata?.secCHUAMobile) {
            this.secCHUAMobile = truncateValue(
                requestMetadata?.secCHUAMobile,
                truncationSize('SecCHUAMobile')
            );
        } else if (request.headers['sec-ch-ua-mobile']) {
            this.secCHUAMobile = truncateValue(
                request.headers['sec-ch-ua-mobile'] as string,
                truncationSize('SecCHUAMobile')
            );
        }
        if (requestMetadata?.secCHUAPlatform) {
            this.secCHUAPlatform = truncateValue(
                requestMetadata?.secCHUAPlatform,
                truncationSize('SecCHUAPlatform')
            );
        } else if (request.headers['sec-ch-ua-platform']) {
            this.secCHUAPlatform = truncateValue(
                request.headers['sec-ch-ua-platform'] as string,
                truncationSize('SecCHUAPlatform')
            );
        }
        if (requestMetadata?.secCHUAArch) {
            this.secCHUAArch = truncateValue(
                requestMetadata?.secCHUAArch,
                truncationSize('SecCHUAArch')
            );
        } else if (request.headers['sec-ch-ua-arch']) {
            this.secCHUAArch = truncateValue(
                request.headers['sec-ch-ua-arch'] as string,
                truncationSize('SecCHUAArch')
            );
        }
        if (requestMetadata?.secCHUAFullVersionList) {
            this.secCHUAFullVersionList = truncateValue(
                requestMetadata?.secCHUAFullVersionList,
                truncationSize('SecCHUAFullVersionList')
            );
        } else if (request.headers['sec-ch-ua-full-version-list']) {
            this.secCHUAFullVersionList = truncateValue(
                request.headers['sec-ch-ua-full-version-list'] as string,
                truncationSize('SecCHUAFullVersionList')
            );
        }
        if (requestMetadata?.secCHUAModel) {
            this.secCHUAModel = truncateValue(
                requestMetadata?.secCHUAModel,
                truncationSize('SecCHUAModel')
            );
        } else if (request.headers['sec-ch-ua-model']) {
            this.secCHUAModel = truncateValue(
                request.headers['sec-ch-ua-model'] as string,
                truncationSize('SecCHUAModel')
            );
        }
        if (requestMetadata?.secCHDeviceMemory) {
            this.secCHDeviceMemory = truncateValue(
                requestMetadata?.secCHDeviceMemory,
                truncationSize('SecCHDeviceMemory')
            );
        } else if (request.headers['sec-ch-device-memory']) {
            this.secCHDeviceMemory = truncateValue(
                request.headers['sec-ch-device-memory'] as string,
                truncationSize('SecCHDeviceMemory')
            );
        }
        this.request =
            requestMetadata?.request ?? request.url?.slice(0, truncationSize('Request')) ?? '/';

        const sessionByHeaderClientId = request.headers['x-datadome-clientid'];
        if (requestMetadata?.clientID) {
            this.clientID = truncateValue(requestMetadata?.clientID, truncationSize('ClientID'));
        } else if (sessionByHeaderClientId !== undefined && sessionByHeaderClientId != '') {
            this.clientID = truncateValue(
                sessionByHeaderClientId as string,
                truncationSize('ClientID')
            );
        } else {
            this.clientID = truncateValue(this.getClientId(request), truncationSize('ClientID'));
        }
    }

    private getProtocol(request: IncomingMessage): string {
        let protocol = (request.socket as TLSSocket).encrypted ? 'https' : 'http';
        const forwardedProto = request.headers['x-forwarded-proto'] as string;
        if (
            forwardedProto !== '' &&
            (forwardedProto?.toLowerCase() === 'http' || forwardedProto?.toLowerCase() === 'https')
        ) {
            protocol = forwardedProto;
        }
        return protocol;
    }

    /**
     * Use the Host header or X-Forwarded-Port if available, otherwise fallback to socket.remotePort
     * @param request
     * @returns The port number
     */
    private getPort(request: IncomingMessage): number {
        // Use the Host header or X-Forwarded-Port if available, otherwise fallback to socket.remotePort
        const hostHeader = request.headers['host'];
        const forwardedPort = request.headers['x-forwarded-port'] as string;
        if (forwardedPort) {
            return parseInt(forwardedPort, 10);
        } else if (hostHeader && hostHeader.includes(':')) {
            return parseInt(hostHeader.split(':')[1], 10);
        } else if (request.socket.localPort) {
            return request.socket.localPort;
        }
        return request.socket.remotePort ?? 0;
    }

    private getClientId(request: IncomingMessage): string {
        const cookies = request.headers['cookie'];
        let clientId = '';
        if (cookies !== undefined) {
            const parsedCookies = parseCookieString(cookies);
            for (const [key, value] of parsedCookies.entries()) {
                if (value !== undefined && key === 'datadome') {
                    clientId = value;
                }
            }
        }
        return clientId;
    }
}

export class DataDomeRequest {
    module: DataDomeModule;
    header: DataDomeHeaders;
    [key: string]: unknown;
    constructor(request: IncomingMessage, requestMetadata?: RequestMetadata) {
        this.module = new DataDomeModule();
        this.header = new DataDomeHeaders(request, requestMetadata);
    }
}
